<template>
  <v-container>
    <v-layout row justify-center>
      <v-flex xs12 md10 lg8>
        <!-- Loading Skeleton -->
        <v-card v-if="isLoading" class="mx-auto mb-4">
          <v-card-title>
            <v-progress-circular
              indeterminate
              color="primary"
              class="mr-3"
            ></v-progress-circular>
            Carregando dados do pagamento DARF...
          </v-card-title>
          <v-card-text>
            <v-progress-linear
              indeterminate
              color="primary"
              class="mb-3"
            ></v-progress-linear>
            <div class="text-xs-center">
              <p class="subheading">Aguarde enquanto buscamos as informações...</p>
            </div>
          </v-card-text>
        </v-card>

        <!-- Content -->
        <v-card v-else id="div_print" class="mx-auto mb-4" elevation="2">
          <!-- Header -->
          <v-card-title class="primary white--text d-flex align-center">
            <v-icon left color="white" size="28">account_balance</v-icon>
            <span class="headline font-weight-medium">
              Pagamento DARF #{{ darf.id ? String(darf.id).padStart(6, '0') : '------' }}
            </span>
            <v-spacer></v-spacer>
            <!-- Print button in header -->
            <v-btn 
              icon 
              color="white" 
              @click="printDiv"
              class="noprint"
              title="Imprimir comprovante"
            >
              <v-icon>print</v-icon>
            </v-btn>
          </v-card-title>

          <!-- Status Badge -->
          <div class="px-4 pt-3 pb-1">
            <v-chip 
              :class="setClass(darf.status)" 
              small 
              label
              text-color="white"
              class="font-weight-medium"
            >
              <v-icon left x-small>{{ getStatusIcon(darf.status) }}</v-icon>
              {{ $t(`status.${darf.status}`) }}
            </v-chip>
          </div>

          <!-- Payment Details -->
          <v-card-text class="pa-3">
            <v-layout row wrap>
              <!-- Valor Total -->
              <v-flex xs12 sm6>
                <div class="detail-item">
                  <div class="detail-label">
                    <v-icon small color="primary" class="mr-2">attach_money</v-icon>
                    Valor Total
                  </div>
                  <div class="detail-value headline primary--text font-weight-bold">
                    {{ $n((darf.total_amount), 'currency') }}
                  </div>
                </div>
              </v-flex>

              <!-- Código da Receita -->
              <v-flex xs12 sm6>
                <div class="detail-item">
                  <div class="detail-label">
                    <v-icon small color="grey darken-1" class="mr-2">receipt</v-icon>
                    Código da Receita
                  </div>
                  <div class="detail-value font-family-monospace">
                    {{ darf.codigo_receita }}
                  </div>
                </div>
              </v-flex>

              <!-- Empresa/Contribuinte -->
              <v-flex xs12>
                <div class="detail-item">
                  <div class="detail-label">
                    <v-icon small color="grey darken-1" class="mr-2">business</v-icon>
                    Empresa/Contribuinte
                  </div>
                  <div class="detail-value">
                    {{ darf.nome_empresa }}
                  </div>
                </div>
              </v-flex>

              <!-- CNPJ/CPF -->
              <v-flex xs12 sm6>
                <div class="detail-item">
                  <div class="detail-label">
                    <v-icon small color="grey darken-1" class="mr-2">assignment_ind</v-icon>
                    CNPJ/CPF
                  </div>
                  <div class="detail-value font-family-monospace">
                    {{ formatCnpjCpf(darf.cnpj_cpf) }}
                  </div>
                </div>
              </v-flex>

              <!-- Data de Vencimento -->
              <v-flex xs12 sm6>
                <div class="detail-item">
                  <div class="detail-label">
                    <v-icon small color="grey darken-1" class="mr-2">event</v-icon>
                    Data de Vencimento
                  </div>
                  <div class="detail-value">
                    {{ setDateFormat(darf.data_vencimento) }}
                  </div>
                </div>
              </v-flex>

              <!-- Descrição (compacta) -->
              <v-flex xs12 v-if="darf.descricao && darf.descricao.length > 0">
                <div class="detail-item-compact">
                  <div class="detail-label-compact">
                    <v-icon x-small color="grey darken-1" class="mr-1">description</v-icon>
                    Descrição
                  </div>
                  <div class="detail-value-compact">
                    {{ darf.descricao }}
                  </div>
                </div>
              </v-flex>

              <!-- Valores Detalhados -->
              <v-flex xs12>
                <v-divider class="my-2"></v-divider>
                <h4 class="body-2 mb-2 font-weight-bold">Detalhamento dos Valores</h4>
              </v-flex>

              <v-flex xs12 sm4>
                <div class="detail-item-compact">
                  <div class="detail-label-compact">
                    <v-icon x-small color="success" class="mr-1">money</v-icon>
                    Principal
                  </div>
                  <div class="detail-value-compact success--text">
                    {{ $n((darf.valor_principal), 'currency') }}
                  </div>
                </div>
              </v-flex>

              <v-flex xs12 sm4 v-if="darf.valor_multa && darf.valor_multa > 0">
                <div class="detail-item-compact">
                  <div class="detail-label-compact">
                    <v-icon x-small color="warning" class="mr-1">warning</v-icon>
                    Multa
                  </div>
                  <div class="detail-value-compact warning--text">
                    {{ $n((darf.valor_multa), 'currency') }}
                  </div>
                </div>
              </v-flex>

              <v-flex xs12 sm4 v-if="darf.valor_juros && darf.valor_juros > 0">
                <div class="detail-item-compact">
                  <div class="detail-label-compact">
                    <v-icon x-small color="orange" class="mr-1">trending_up</v-icon>
                    Juros
                  </div>
                  <div class="detail-value-compact orange--text">
                    {{ $n((darf.valor_juros), 'currency') }}
                  </div>
                </div>
              </v-flex>

              <!-- Comprovante de Registro -->
              <v-flex xs12 v-if="darf.protocol || darf.return_code">
                <v-divider class="my-2"></v-divider>
                <h4 class="body-2 mb-2 font-weight-bold">
                  <v-icon small color="success" class="mr-1">verified</v-icon>
                  Comprovante de Registro
                </h4>
              </v-flex>

              <!-- Protocol -->
              <v-flex xs12 sm6 v-if="darf.protocol">
                <div class="detail-item-compact">
                  <div class="detail-label-compact">
                    <v-icon x-small color="success" class="mr-1">fingerprint</v-icon>
                    Protocolo
                  </div>
                  <div class="detail-value-compact font-family-monospace">
                    {{ darf.protocol }}
                    <v-btn 
                      icon 
                      x-small 
                      @click="copyToClipboard(darf.protocol)"
                      title="Copiar protocolo"
                      class="ml-1 noprint"
                    >
                      <v-icon x-small>content_copy</v-icon>
                    </v-btn>
                  </div>
                </div>
              </v-flex>

              <!-- Código de Retorno -->
              <v-flex xs12 sm6 v-if="darf.return_code">
                <div class="detail-item-compact">
                  <div class="detail-label-compact">
                    <v-icon x-small color="info" class="mr-1">code</v-icon>
                    Cód. Retorno
                  </div>
                  <div class="detail-value-compact font-family-monospace">
                    {{ darf.return_code }}
                  </div>
                </div>
              </v-flex>

              <!-- Autenticação Bancária -->
              <v-flex xs12 v-if="darf.authentication_code">
                <div class="detail-item-compact">
                  <div class="detail-label-compact">
                    <v-icon x-small color="primary" class="mr-1">security</v-icon>
                    Cód. Autenticação
                  </div>
                  <div class="detail-value-compact font-family-monospace">
                    {{ darf.authentication_code }}
                    <v-btn 
                      icon 
                      x-small 
                      @click="copyToClipboard(darf.authentication_code)"
                      title="Copiar código de autenticação"
                      class="ml-1 noprint"
                    >
                      <v-icon x-small>content_copy</v-icon>
                    </v-btn>
                  </div>
                </div>
              </v-flex>

              <!-- Data de Criação -->
              <v-flex xs12 sm6>
                <div class="detail-item-compact">
                  <div class="detail-label-compact">
                    <v-icon x-small color="grey darken-1" class="mr-1">schedule</v-icon>
                    Criado em
                  </div>
                  <div class="detail-value-compact">
                    {{ setDateFormat(darf.created_at) }}
                  </div>
                </div>
              </v-flex>

              <!-- Data de Pagamento -->
              <v-flex xs12 sm6 v-if="darf.data_pagamento">
                <div class="detail-item-compact">
                  <div class="detail-label-compact">
                    <v-icon x-small color="success" class="mr-1">payment</v-icon>
                    Pago em
                  </div>
                  <div class="detail-value-compact success--text">
                    {{ setDateFormat(darf.data_pagamento) }}
                  </div>
                </div>
              </v-flex>

              <!-- Mensagem de Retorno (apenas se importante) -->
              <v-flex xs12 v-if="darf.return_message && darf.status === 'rejected'">
                <div class="detail-item-compact">
                  <div class="detail-label-compact">
                    <v-icon x-small color="error" class="mr-1">info</v-icon>
                    Mensagem
                  </div>
                  <div class="detail-value-compact error--text">
                    {{ darf.return_message }}
                  </div>
                </div>
              </v-flex>

              <!-- Status de Registro -->
              <v-flex xs12 v-if="!darf.protocol && darf.status === 'pending'">
                <v-divider class="my-2"></v-divider>
                <v-alert color="warning" icon="schedule" dense class="body-2">
                  <strong>Aguardando processamento bancário</strong>
                </v-alert>
              </v-flex>
            </v-layout>
          </v-card-text>

          <!-- Action Buttons -->
          <v-card-actions class="pa-3 noprint">
            <v-btn 
              color="primary" 
              outlined 
              small
              @click="$router.go(-1)"
              class="mr-2"
            >
              <v-icon left small>arrow_back</v-icon>
              Voltar
            </v-btn>
            <v-spacer></v-spacer>
            <v-btn 
              color="blue-grey" 
              outlined 
              small
              @click="printDiv"
            >
              <v-icon left small>print</v-icon>
              Imprimir
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-flex>
    </v-layout>
  </v-container>
</template>

<script>
import { mapGetters } from "vuex";

export default {
  name: "ShowDarfPayments",
  created() {
    this.loadDarfPayment();
  },
  computed: {
    ...mapGetters({
      darf: "bills/showDarfPayment",
      isLoading: "bills/darfPaymentLoadingData",
      systemAppName: "system/appName",
    }),
    darfId() {
      return this.$route.params.id;
    },
    appName() {
      return this.systemAppName || process.env.VUE_APP_APP_NAME || 'FlowIt';
    },
  },
  methods: {
    loadDarfPayment() {
      this.$store.dispatch("bills/loadDarfPayment", this.darfId);
    },
    setClass(status) {
      return `invoice ${status}`;
    },
    getStatusIcon(status) {
      const icons = {
        'pending': 'hourglass_empty',
        'check_payment': 'search',
        'paid': 'check_circle',
        'rejected': 'cancel',
        'refounded': 'undo',
      };
      return icons[status] || 'help';
    },
    setDateFormat(date) {
      if (!date) return '';
      return new Date(date).toLocaleDateString('pt-BR');
    },
    setTimeFormat(date) {
      if (!date) return '';
      return new Date(date).toLocaleTimeString('pt-BR', { 
        hour: '2-digit', 
        minute: '2-digit' 
      });
    },
    formatCnpjCpf(value) {
      if (!value) return '';
      const clean = value.replace(/\D/g, '');
      if (clean.length === 11) {
        return clean.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
      } else if (clean.length === 14) {
        return clean.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5');
      }
      return value;
    },
    copyToClipboard(text) {
      navigator.clipboard.writeText(text).then(() => {
        this.$store.dispatch('showSnackbar', {
          message: 'Código copiado para a área de transferência!',
          color: 'success'
        });
      }).catch(() => {
        this.$store.dispatch('showSnackbar', {
          message: 'Erro ao copiar código',
          color: 'error'
        });
      });
    },
    printDiv() {
      window.print();
    }
  }
};
</script>

<style scoped>
.detail-item {
  margin-bottom: 16px;
}

.detail-item-compact {
  margin-bottom: 8px;
}

.detail-label {
  font-size: 14px;
  color: #666;
  font-weight: 500;
  margin-bottom: 4px;
  display: flex;
  align-items: center;
}

.detail-label-compact {
  font-size: 12px;
  color: #666;
  font-weight: 500;
  margin-bottom: 2px;
  display: flex;
  align-items: center;
}

.detail-value {
  font-size: 16px;
  color: #333;
  font-weight: 400;
}

.detail-value-compact {
  font-size: 14px;
  color: #333;
  font-weight: 400;
}

.font-family-monospace {
  font-family: 'Courier New', Courier, monospace !important;
}

/* Print styles */
@media print {
  .noprint {
    display: none !important;
  }
  
  .v-card {
    box-shadow: none !important;
    border: 1px solid #ddd !important;
    margin: 0 !important;
  }
  
  .v-card-text {
    padding: 12px !important;
  }
  
  .detail-item, .detail-item-compact {
    margin-bottom: 6px !important;
  }
  
  .detail-label, .detail-label-compact {
    font-size: 11px !important;
    margin-bottom: 1px !important;
  }
  
  .detail-value, .detail-value-compact {
    font-size: 12px !important;
  }
  
  .headline {
    font-size: 18px !important;
  }
  
  .subheading, .body-2 {
    font-size: 13px !important;
  }
  
  .v-divider {
    margin: 8px 0 !important;
  }
  
  .v-alert {
    padding: 8px !important;
    font-size: 11px !important;
  }
  
  .primary {
    background-color: #1976d2 !important;
    -webkit-print-color-adjust: exact;
  }
  
  .white--text {
    color: white !important;
    -webkit-print-color-adjust: exact;
  }
  
  .v-chip {
    font-size: 11px !important;
    height: auto !important;
    padding: 4px 8px !important;
  }
}

/* DARF Payment Status Colors */
.v-chip.invoice.pending { background-color: #1976D2 !important; } /* primary - blue */
.v-chip.invoice.check_payment { background-color: #29B6F6 !important; } /* info - light blue */
.v-chip.invoice.waiting_payment_day { background-color: #FFC107 !important; } /* warning - yellow */
.v-chip.invoice.paid { background-color: #4CAF50 !important; } /* success - green */
.v-chip.invoice.rejected { background-color: #F44336 !important; } /* error - red */
.v-chip.invoice.unknow { background-color: #9E9E9E !important; } /* secondary - gray */
</style>